import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Clock, Mail, Trash2, CheckCircle } from 'lucide-react';

const WaitlistTab = ({
  waitlistEntries,
  waitlistLoading,
  notifyingId,
  fetchWaitlist,
  notifyWaitlistParent,
  removeFromWaitlist
}) => {
  return (
    <div data-testid="waitlist-tab">
      <Card className="shadow-lg bg-[#111] border-[#333]">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-white">Waitlist Management</CardTitle>
            <p className="text-sm text-white/50 mt-1">Parents waiting for spots in full classes</p>
          </div>
          <Button 
            variant="outline" 
            onClick={fetchWaitlist}
            disabled={waitlistLoading}
            className="border-[#333] text-white hover:bg-[#222]"
          >
            {waitlistLoading ? 'Loading...' : 'Refresh'}
          </Button>
        </CardHeader>
        <CardContent>
          {waitlistLoading ? (
            <div className="space-y-3">
              {[1,2,3].map(i => (
                <div key={i} className="h-20 bg-[#1a1a1a] rounded-lg animate-pulse" />
              ))}
            </div>
          ) : waitlistEntries.length === 0 ? (
            <div className="text-center py-12">
              <Clock className="w-12 h-12 text-white/20 mx-auto mb-4" />
              <p className="text-white/50">No waitlist entries</p>
              <p className="text-sm text-white/30 mt-1">
                Parents will appear here when they join a waitlist for full classes
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {waitlistEntries.map(entry => (
                <div 
                  key={entry.id} 
                  className="p-4 bg-[#1a1a1a] rounded-lg border border-[#333] hover:border-[#7000FF]/50 transition-all"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold text-white">{entry.student_name}</span>
                        <Badge variant="outline" className="text-[#7000FF] border-[#7000FF]/50">
                          #{entry.position} in queue
                        </Badge>
                        {entry.notified_at && (
                          <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Notified
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-white/70">
                        <span className="text-[#FF007F]">{entry.class_name}</span>
                        {entry.schedule_day && ` • ${entry.schedule_day}`}
                        {entry.schedule_time && ` at ${entry.schedule_time}`}
                      </p>
                      <p className="text-xs text-white/50 mt-1">
                        Parent: {entry.parent_name} ({entry.parent_email})
                      </p>
                      <p className="text-xs text-white/30 mt-1">
                        Joined: {new Date(entry.created_at).toLocaleDateString('en-GB', { 
                          day: 'numeric', month: 'short', year: 'numeric' 
                        })}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        onClick={() => notifyWaitlistParent(entry.id)}
                        disabled={notifyingId === entry.id}
                        className="bg-[#7000FF] hover:bg-[#7000FF]/80"
                      >
                        {notifyingId === entry.id ? (
                          '...'
                        ) : (
                          <>
                            <Mail className="w-4 h-4 mr-1" />
                            Notify Spot Open
                          </>
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => removeFromWaitlist(entry.id)}
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default WaitlistTab;
